import { useMemo, useState } from 'react'
import { Check, Copy, RefreshCw, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/cn'
import type { RecipeExecutionLogEntry, RecipeExecutionStatus } from '@/services/api/workflows'

interface RecipeExecutionLogsPanelProps {
  execution?: RecipeExecutionStatus | null
  executionId?: string
  recipeName?: string
  logs?: Array<Record<string, any> | string>
  expired?: boolean
  isLoading?: boolean
  isRefreshing?: boolean
  error?: string | null
  onRefresh?: () => void
  onClose?: () => void
}

function buildLogLine(entry: RecipeExecutionLogEntry | string) {
  if (typeof entry === 'string') {
    return entry
  }

  const timestamp =
    typeof entry.ts === 'number' ? new Date(entry.ts * 1000).toLocaleTimeString() : null
  const level = entry.level?.toUpperCase()
  const source = entry.component || entry.source || entry.action_type
  const parts = [timestamp, level, source].filter(Boolean)
  const prefix = parts.length > 0 ? `[${parts.join(' · ')}] ` : ''
  return `${prefix}${entry.message || ''}`.trim()
}

export default function RecipeExecutionLogsPanel({
  execution,
  executionId,
  recipeName,
  logs: directLogs,
  expired = false,
  isLoading = false,
  isRefreshing = false,
  onRefresh,
  onClose,
}: RecipeExecutionLogsPanelProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const logs = directLogs ?? execution?.logs ?? []
  const logLines = useMemo(() => logs.map((entry) => buildLogLine(entry as any)), [logs])
  const executionLabel =
    recipeName ||
    execution?.workflow_name ||
    execution?.workflow_id ||
    executionId ||
    t('common.run')

  const handleCopyLogs = async () => {
    if (logLines.length === 0) return
    try {
      await navigator.clipboard.writeText(logLines.join('\n'))
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch (copyError) {
      console.error('Unable to copy logs', copyError)
    }
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-foreground">
              {t('executionLogs.title')}
            </p>
            <h3 className="truncate text-sm font-semibold text-foreground">
              {executionLabel}
            </h3>
          </div>
          <div className="flex items-center gap-1.5">
            {onRefresh && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onRefresh}
                disabled={isLoading || isRefreshing}
                title={t('executionLogs.refresh')}
              >
                <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => void handleCopyLogs()}
              disabled={logLines.length === 0}
              title={t('executionLogs.copy')}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
            {onClose && (
              <Button variant="ghost" size="icon" onClick={onClose} title={t('common.close')}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {isLoading && logLines.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {t('executionLogs.loading')}
          </div>
        ) : logLines.length === 0 && expired ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {t('executionLogs.expired')}
          </div>
        ) : logLines.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {t('executionLogs.empty')}
          </div>
        ) : (
          <pre className="h-full overflow-auto whitespace-pre-wrap rounded-xl bg-muted p-4 text-xs text-foreground">
            {logLines.join('\n')}
          </pre>
        )}
      </div>
    </div>
  )
}
